import { useEffect, useState, useContext, ChangeEvent } from 'react';
import { DataGrid, GridColDef, GridFilterModel } from '@mui/x-data-grid';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import DataContext from '../../services/DataContext';
import CustomToolbar from '../uiTools/LicensesCommon/CustomToolbar';
import SnackBar from '../SnackBar/SnackBar';
import { SnackBarProps } from '../../types/snackBarTypes';
import { useDebounce } from '../Hooks/useDebouce';
import { buildMongoWhere } from '../../services/buildMongoWhere';
import {
  FunctionUsageRow,
  StatsByFunction,
  StatsSummary,
} from '../../types/statsTypes';

dayjs.locale('fr');

const formatDate = (value?: string | Date | null) =>
  value ? dayjs(new Date(value)).format('DD/MM/YYYY HH:mm') : '';

interface KpiCardProps {
  label: string;
  value: number;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value }) => (
  <Card
    sx={{
      minWidth: 150,
      flex: '1 1 150px',
      backgroundColor: 'var(--secondary-dark-color)',
      color: 'var(--off-white-color)',
    }}
  >
    <CardContent>
      <Typography variant="h3" component="p" sx={{ fontWeight: 700 }}>
        {value.toLocaleString('fr-FR')}
      </Typography>
      <Typography variant="body1" component="p">
        {label}
      </Typography>
    </CardContent>
  </Card>
);

const emptySummary: StatsSummary = {
  byFunction: [],
  totals: {
    totalCalls: 0,
    distinctClients: 0,
    distinctMachines: 0,
    distinctSoftwares: 0,
    distinctFunctions: 0,
  },
};

export const Stats: React.FC = () => {
  const { getStatsSummary, getAll } = useContext(DataContext);

  const [snackbarInfo, setSnackbarInfo] = useState<SnackBarProps>({
    key: 0,
    message: null,
    severity: null,
  });

  const notifyError = (message: string) =>
    setSnackbarInfo({
      key: new Date().getTime(),
      message,
      severity: 'error',
    });

  // ---- Aggregated dashboard ----
  const [summary, setSummary] = useState<StatsSummary>(emptySummary);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    const fetchSummary = async () => {
      setSummaryLoading(true);
      try {
        setSummary(await getStatsSummary());
      } catch (error) {
        notifyError(
          `Erreur dans l'extraction des statistiques agrégées : ${error}`,
        );
      } finally {
        setSummaryLoading(false);
      }
    };

    fetchSummary();
  }, [getStatsSummary]);

  const summaryColumns: GridColDef[] = [
    { field: 'softwareId', headerName: 'Logiciel', width: 160 },
    {
      field: 'functionId',
      headerName: 'ID fonction',
      width: 110,
      type: 'number',
    },
    { field: 'functionDisplay', headerName: 'Fonction', width: 220 },
    { field: 'totalCalls', headerName: 'Appels', width: 110, type: 'number' },
    {
      field: 'distinctMachines',
      headerName: 'Machines',
      width: 110,
      type: 'number',
    },
    {
      field: 'distinctClients',
      headerName: 'Clients',
      width: 110,
      type: 'number',
    },
    // Keep real Date values so sorting is chronological; format only for display.
    {
      field: 'firstCall',
      headerName: 'Première utilisation',
      width: 170,
      type: 'dateTime',
      valueGetter: params =>
        params.row.firstCall ? new Date(params.row.firstCall) : null,
      valueFormatter: params => formatDate(params.value),
    },
    {
      field: 'lastCall',
      headerName: 'Dernière utilisation',
      width: 170,
      type: 'dateTime',
      valueGetter: params =>
        params.row.lastCall ? new Date(params.row.lastCall) : null,
      valueFormatter: params => formatDate(params.value),
    },
  ];

  const summaryRows = summary.byFunction.map((row: StatsByFunction) => ({
    ...row,
    id: `${row.softwareId}-${row.functionId}`,
  }));

  // ---- Raw traces table (server-side pagination + search) ----
  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedSearchValue = useDebounce(searchValue, 400);
  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) =>
    setSearchValue(event.target.value);

  const [rawRows, setRawRows] = useState<FunctionUsageRow[]>([]);
  const [rawLoading, setRawLoading] = useState(false);
  const [rowCount, setRowCount] = useState<number>(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (!initialized) return;

    const fetchRaw = async () => {
      setRawLoading(true);
      try {
        const result = await getAll(
          'stats',
          paginationModel.pageSize,
          paginationModel.page * paginationModel.pageSize,
          debouncedSearchValue,
          'functionLastCall',
          'DESC',
          buildMongoWhere(filterModel),
        );

        const transformed = (result.data as FunctionUsageRow[]).map(row => ({
          ...row,
          functionFirstCall: formatDate(row.functionFirstCall),
          functionLastCall: formatDate(row.functionLastCall),
        }));

        setRawRows(transformed);
        setRowCount(result.totalCount);
      } catch (error) {
        notifyError(
          `Erreur dans l'extraction des traces d'utilisation : ${error}`,
        );
      } finally {
        setRawLoading(false);
      }
    };

    fetchRaw();
  }, [initialized, paginationModel, debouncedSearchValue, filterModel, getAll]);

  // Reset to the first page whenever the (debounced) search changes
  useEffect(() => {
    setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [debouncedSearchValue]);

  const rawColumns: GridColDef[] = [
    { field: 'clientNumber', headerName: 'N° client', width: 130 },
    { field: 'cadworkDisplay', headerName: 'Machine', width: 140 },
    { field: 'cadworkId', headerName: 'Cadwork ID', width: 170 },
    { field: 'softwareId', headerName: 'Logiciel', width: 140 },
    { field: 'functionId', headerName: 'ID fonction', width: 110 },
    { field: 'functionDisplay', headerName: 'Fonction', width: 200 },
    { field: 'functionCall', headerName: 'Appels', width: 100, type: 'number' },
    {
      field: 'functionFirstCall',
      headerName: 'Première',
      width: 160,
      filterable: false,
    },
    {
      field: 'functionLastCall',
      headerName: 'Dernière',
      width: 160,
      filterable: false,
    },
  ];

  const rawInitialState = {
    columns: {
      columnVisibilityModel: {
        cadworkId: false,
      },
    },
  };

  return (
    <>
      <Typography
        variant="h1"
        component="h1"
        sx={{
          color: 'var(--off-white-color)',
          textAlign: 'center',
          mb: 6,
        }}
      >
        Statistiques d'utilisation
      </Typography>

      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 6 }}
      >
        <KpiCard label="Appels totaux" value={summary.totals.totalCalls} />
        <KpiCard
          label="Fonctions suivies"
          value={summary.totals.distinctFunctions}
        />
        <KpiCard label="Logiciels" value={summary.totals.distinctSoftwares} />
        <KpiCard
          label="Machines distinctes"
          value={summary.totals.distinctMachines}
        />
        <KpiCard
          label="Clients distincts"
          value={summary.totals.distinctClients}
        />
      </Stack>

      <Typography
        variant="h2"
        component="h2"
        sx={{ color: 'var(--off-white-color)', mb: 2 }}
      >
        Par fonction
      </Typography>

      <Box sx={{ width: '100%', mb: 8 }}>
        <DataGrid
          columns={summaryColumns}
          rows={summaryRows}
          getRowId={row => row.id}
          loading={summaryLoading}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
          }}
          slots={{
            noRowsOverlay: () => (
              <Stack
                height="100%"
                alignItems="center"
                justifyContent="center"
                fontSize="1.5rem"
              >
                Aucune donnée d'utilisation pour le moment...
              </Stack>
            ),
          }}
          autoHeight
        />
      </Box>

      <Typography
        variant="h2"
        component="h2"
        sx={{ color: 'var(--off-white-color)', mb: 2 }}
      >
        Traces détaillées
      </Typography>

      <Box sx={{ width: '100%' }}>
        <DataGrid
          columns={rawColumns}
          rows={rawRows}
          getRowId={row =>
            row.id ?? `${row.clientNumber}-${row.cadworkId}-${row.functionId}`
          }
          slots={{
            toolbar: () => (
              <CustomToolbar
                searchValue={searchValue}
                handleSearchChange={handleSearchChange}
              />
            ),
            noRowsOverlay: () => (
              <Stack
                height="100%"
                alignItems="center"
                justifyContent="center"
                fontSize="1.5rem"
              >
                Aucune trace à afficher...
              </Stack>
            ),
          }}
          pageSizeOptions={[10, 25, 50]}
          paginationModel={paginationModel}
          onPaginationModelChange={newPagination => {
            if (
              initialized &&
              (newPagination.page !== paginationModel.page ||
                newPagination.pageSize !== paginationModel.pageSize)
            ) {
              setPaginationModel(newPagination);
            }
          }}
          rowCount={rowCount}
          paginationMode="server"
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={newFilterModel => {
            setFilterModel(newFilterModel);
            setPaginationModel(prev =>
              prev.page === 0 ? prev : { ...prev, page: 0 },
            );
          }}
          loading={rawLoading}
          initialState={rawInitialState}
          autoHeight
        />
      </Box>

      {snackbarInfo.message && snackbarInfo.severity && (
        <SnackBar
          key={snackbarInfo.key}
          message={snackbarInfo.message}
          severity={snackbarInfo.severity}
        />
      )}
    </>
  );
};

export default Stats;
